/**
 * Program 12.1 UX4 — Commercial proposal cockpit with terms + deviations.
 */
import React, { useEffect, useState } from 'react';
import { Button, Card, Descriptions, InputNumber, Space, Tag, Typography, message, Alert, Modal } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';
import { buildProposalReadiness, readinessTagColor } from './estimateProposalSubmissionReadiness';
import {
  mergeDeviationsMeta,
  parseCommercialDeviations,
  type CommercialDeviationRow,
} from './commercialDeviations';
import { EpcCommercialDeviationsMatrix } from './EpcCommercialDeviationsMatrix';
import { EpcApprovedRevisionPicker } from '@/components/epc/estimation/EpcApprovedRevisionPicker';

const { Text, Title } = Typography;

export type EpcCommercialProposalCockpitProps = {
  tenderId: string;
  proposal: Record<string, unknown>;
};

export const EpcCommercialProposalCockpit: React.FC<EpcCommercialProposalCockpitProps> = ({
  tenderId,
  proposal,
}) => {
  const qc = useQueryClient();
  const id = String(proposal.id || '');
  const meta = (proposal.meta_json || proposal.metaJson || {}) as Record<string, unknown>;
  const terms = (meta.commercial_terms || {}) as Record<string, unknown>;
  const deviations = parseCommercialDeviations(meta);
  const [valueDraft, setValueDraft] = useState<number | null>(
    proposal.commercial_value != null ? Number(proposal.commercial_value) : null
  );
  const [certifyRevId, setCertifyRevId] = useState<string | undefined>();

  useEffect(() => {
    setValueDraft(proposal.commercial_value != null ? Number(proposal.commercial_value) : null);
  }, [proposal.id, proposal.commercial_value]);

  const readiness = buildProposalReadiness(proposal);
  const editable = !['Submitted', 'Approved', 'Issued'].includes(String(proposal.status || ''));

  const basisQ = useQuery({
    queryKey: ['epc-proposal-submission-basis', id],
    queryFn: () => epcCommercialService.getProposalSubmissionBasis(id),
    enabled: !!id,
  });

  const estPinEarly = (meta.est3_pricing_pin || meta.est3PricingPin || null) as Record<
    string,
    unknown
  > | null;
  const estimationIdEarly = String(
    proposal.estimation_id || proposal.estimationId || estPinEarly?.estimate_id || ''
  );

  const estimatesQ = useQuery({
    queryKey: ['epc-estimates', tenderId],
    queryFn: () => epcEstimationService.list(tenderId),
    enabled: !!tenderId && editable,
  });
  const resolveEstimateId =
    estimationIdEarly || (estimatesQ.data?.[0]?.id ? String(estimatesQ.data[0].id) : '');

  const wsQ = useQuery({
    queryKey: ['epc-estimate-ws', resolveEstimateId],
    queryFn: () => epcEstimationService.getWorkspace(resolveEstimateId),
    enabled: !!resolveEstimateId && editable,
  });

  useEffect(() => {
    const pinRev0 = String(estPinEarly?.estimate_revision_id || '');
    if (pinRev0) setCertifyRevId(pinRev0);
    else {
      const approved = (wsQ.data?.revisions || []).find(
        (r: any) => String(r.status || '').toLowerCase() === 'approved'
      );
      if (approved) setCertifyRevId(String(approved.id));
    }
  }, [estPinEarly?.estimate_revision_id, wsQ.data?.revisions]);

  const patchM = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      epcCommercialService.patchProposal(id, payload),
    onSuccess: () => {
      message.success('Commercial proposal updated');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
    },
    onError: (e: Error) => message.error(e.message || 'Update failed'),
  });

  const certifyM = useMutation({
    mutationFn: (payload: {
      estimate_id: string;
      estimate_revision_id: string;
      replace?: boolean;
    }) => epcCommercialService.certifyProposalEstimateBasis(id, payload),
    onSuccess: () => {
      message.success('Estimate basis certified');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      qc.invalidateQueries({ queryKey: ['epc-proposal-submission-basis', id] });
    },
    onError: (e: Error) => message.error(e.message || 'Certify failed'),
  });

  const saveTerms = (patch: Record<string, unknown>) => {
    patchM.mutate({
      metaJson: { ...meta, commercial_terms: { ...terms, ...patch } },
    });
  };

  const estPin = (meta.est3_pricing_pin || meta.est3PricingPin || null) as Record<
    string,
    unknown
  > | null;
  const basisStatus = String((basisQ.data as any)?.status || (estPin ? 'CERTIFIED' : 'NOT_RECORDED'));
  const estimationId = resolveEstimateId;
  const pinRev = String(estPin?.estimate_revision_id || '');

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {basisStatus === 'STALE' && (
        <Alert
          type="error"
          showIcon
          message="Submission basis STALE"
          description={(basisQ.data as any)?.stale_reason || 'Addenda issued after certification — re-certify.'}
        />
      )}
      {basisStatus === 'NOT_RECORDED' && (
        <Alert
          type="warning"
          showIcon
          message="Estimate basis not recorded"
          description="Never inferred from current revision. Certify an Approved EST3 revision explicitly."
        />
      )}
      {estPin?.estimate_revision_id ? (
        <Alert
          type={basisStatus === 'CERTIFIED' ? 'success' : 'info'}
          showIcon
          message={`EST3 pricing basis · ${basisStatus} · revision ${String(estPin.revision_label || estPin.estimate_revision_id)} · Final Bid ${
            estPin.final_bid_price != null
              ? formatMoney(Number(estPin.final_bid_price), {
                  currency: String(estPin.currency || proposal.currency || 'AED'),
                })
              : '—'
          }`}
          description="Pinned snapshot — creating a newer estimate revision does not auto-refresh this proposal."
        />
      ) : null}
      {editable && (
        <Card size="small" title="Certify estimate basis">
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Text type="secondary">Uses exact Approved revision — not the working draft.</Text>
            <EpcApprovedRevisionPicker
              revisions={(wsQ.data?.revisions || []) as Array<Record<string, unknown>>}
              value={certifyRevId}
              onChange={setCertifyRevId}
            />
            <Button
              type="primary"
              loading={certifyM.isPending}
              disabled={!estimationId || !certifyRevId}
              onClick={() => {
                if (!estimationId || !certifyRevId) {
                  message.warning('Select an Approved revision');
                  return;
                }
                Modal.confirm({
                  title: 'Certify submission basis?',
                  content:
                    'Pin Approved revision onto this proposal. Customer-submitted pins cannot be replaced.',
                  onOk: () =>
                    certifyM.mutateAsync({
                      estimate_id: estimationId,
                      estimate_revision_id: certifyRevId,
                      replace: !!estPin && pinRev !== certifyRevId,
                    }),
                });
              }}
            >
              Certify estimate basis
            </Button>
          </Space>
        </Card>
      )}
      <Card size="small">
        <Title level={5} style={{ marginTop: 0 }}>
          COMMERCIAL PROPOSAL
        </Title>
        <Space wrap>
          <Tag color={readinessTagColor(readiness.overall)}>{readiness.overall}</Tag>
          <Tag color={basisStatus === 'CERTIFIED' ? 'green' : basisStatus === 'STALE' ? 'red' : 'orange'}>
            Basis {basisStatus}
          </Tag>
          <Tag>Rev {String(proposal.revision || 1)}</Tag>
          <Text>
            {valueDraft != null
              ? formatMoney(valueDraft, { currency: String(proposal.currency || 'AED') })
              : 'No bid price'}
          </Text>
        </Space>
        <Space wrap style={{ marginTop: 12 }}>
          <InputNumber
            min={0}
            disabled={!editable || patchM.isPending}
            value={valueDraft ?? undefined}
            addonAfter={String(proposal.currency || 'AED')}
            onChange={(n) => setValueDraft(n == null ? null : Number(n))}
          />
          <Button
            disabled={!editable || valueDraft == null}
            loading={patchM.isPending}
            onClick={() => valueDraft != null && patchM.mutate({ commercialValue: valueDraft })}
          >
            Save bid price
          </Button>
        </Space>
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          Prefer certifying from EST3 Final Bid — manual bid price is for draft only until certified.
        </Text>
      </Card>

      <Card size="small" title="Commercial terms">
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Validity">{String(terms.validity_days || terms.validity || '—')} days</Descriptions.Item>
          <Descriptions.Item label="Advance">{String(terms.advance_pct || '—')}%</Descriptions.Item>
          <Descriptions.Item label="Retention">{String(terms.retention_pct || '—')}%</Descriptions.Item>
          <Descriptions.Item label="LD cap">{String(terms.ld_cap_pct || '—')}%</Descriptions.Item>
          <Descriptions.Item label="Warranty">{String(terms.warranty_months || '—')} months</Descriptions.Item>
        </Descriptions>
        {editable && (
          <Button
            size="small"
            style={{ marginTop: 8 }}
            onClick={() =>
              saveTerms({
                validity_days: 120,
                advance_pct: 10,
                retention_pct: 5,
                ld_cap_pct: 10,
                warranty_months: 24,
              })
            }
          >
            Apply template terms
          </Button>
        )}
      </Card>

      <Card size="small" title="Commercial deviations">
        <EpcCommercialDeviationsMatrix rows={deviations} />
        {editable && deviations.length === 0 && (
          <Button
            size="small"
            style={{ marginTop: 8 }}
            onClick={() => {
              const rows: CommercialDeviationRow[] = [
                {
                  id: 'pay-1',
                  clause: 'Payment',
                  client_requirement: '120 days',
                  bid_position: '60 days',
                  risk: 'HIGH_RISK',
                  status: 'Pending',
                  approved: false,
                },
              ];
              patchM.mutate({ metaJson: mergeDeviationsMeta(meta, rows) });
            }}
          >
            Add sample deviation
          </Button>
        )}
      </Card>
    </Space>
  );
};

export default EpcCommercialProposalCockpit;
